import React from 'react';

import { Select } from '@aline/common';
import { routes } from '@aline/routes';
import { Typography } from '@mui/material';
import type { GridCellParams } from '@mui/x-data-grid-premium';
import { useMatch } from 'react-router-dom';
import type {
  ContractTypeDocument,
  ReportGridGroupRow,
  ReportGridRow,
  ReportRow,
} from 'src/components';
import {
  ContractTypePicker,
  DocumentNameCell,
  FavoriteCell,
  getIsReportGridColDef,
  getIsReportGridRow,
  GridStatusCell,
  OwnerCell,
  ReportCellValue,
} from 'src/components';
import { PulseIcon, PulseIconVariant } from 'src/components/icons/Pulse';
import { RepositoryPartiesCell } from 'src/components/repository/RepositoryDocumentPartyCell';
import {
  getOCRStatusFromFileVersion,
  OCRStatus,
} from 'src/editor/toolbars/right-panel/ai/shared/OCRStatusOrChildren';
import { Apollo } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';

import { DocumentLabelCell } from './DocumentLabelCell';
import { ExtractionCell, OCRRunningCell } from './ExtractionCell';

const useCellMakeStyles = makeStyles<{ isAtReportRoute: boolean }>()(
  (theme, { isAtReportRoute }) => ({
    cell: {
      alignItems: 'center',
      boxSizing: 'border-box' as const,
      display: 'flex',
      flexWrap: 'nowrap' as const,
      gap: theme.spacing(1),
      height: '100%',
      overflow: 'hidden',
      padding: isAtReportRoute ? theme.spacing(0, 1) : undefined,
      width: '100%',
    },
  })
);

export function useCellStyles() {
  const reportRouteMatch = useMatch({ end: false, path: routes.ReportDetail });
  const isAtReportRoute = Boolean(reportRouteMatch);

  const { classes } = useCellMakeStyles({ isAtReportRoute });
  return classes;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function ReportCell(params: GridCellParams<ReportRow, any>): JSX.Element | null {
  const { colDef, row, value } = params;

  if (!getIsReportGridRow(row)) return null;
  if (!getIsReportGridColDef(colDef)) return <>-</>;

  switch (colDef.__typename) {
    case 'ExtractionReportColumn':
      return <ExtractionCell {...params} row={row} />;
    case 'FileFieldColumn': {
      switch (colDef.field) {
        case Apollo.ReportColumnFileField.Name:
          return <DocumentNameCell {...params} row={row} />;
        case Apollo.ReportColumnFileField.Owner:
          return <DocumentOwnerCell {...params} row={row} />;
        case Apollo.ReportColumnFileField.Parties:
          return <RepositoryPartiesCell {...params} row={row} />;
        case Apollo.ReportColumnFileField.RequesterFavorited:
          return <FavoriteCell {...params} row={row} />;
        case Apollo.ReportColumnFileField.Status:
          return <GridStatusCell {...params} row={row} />;
        case Apollo.ReportColumnFileField.Type:
          return <ContractTypeCell {...params} row={row} />;
        case Apollo.ReportColumnFileField.Labels:
          return <DocumentLabelCell {...params} row={row} />;
        default:
          return <ReportCellValue colDef={colDef} value={value} />;
      }
    }
  }
}

export type ContractTypeRow = ContractTypeDocument | ReportGridGroupRow;

export function ContractTypeCell({
  row,
}: GridCellParams<ContractTypeRow, ContractTypeDocument['type']>): JSX.Element {
  const cellClasses = useCellStyles();
  const reportRouteMatch = useMatch({ end: false, path: routes.ReportDetail });
  const isAtReportRoute = Boolean(reportRouteMatch);

  // Guard against incomplete row data from MUI DataGrid's server-side cache
  if (!getIsReportGridRow(row)) return <div className={cellClasses.cell} />;

  const ocrStatus = getOCRStatusFromFileVersion(row.latestVersion);
  const isAiSettingType = Boolean(
    row.coreAiPropertiesExtractionStartedAt &&
    !row.coreAiPropertiesExtractionFinishedAt &&
    !row.coreAiPropertiesExtractionFailedAt
  );
  const dataCy = Select.report.row.contractType.container;

  if (ocrStatus === OCRStatus.Running)
    return <OCRRunningCell pulsePosition={isAtReportRoute ? 'right' : 'left'} />;
  if (isAiSettingType) {
    return (
      <div className={cellClasses.cell} data-cy={dataCy}>
        <PulseIcon variant={PulseIconVariant.LoadingPrimary} />
        <Typography noWrap variant="body1">
          Ai setting type...
        </Typography>
      </div>
    );
  }

  return (
    <div className={cellClasses.cell} data-cy={dataCy}>
      <ContractTypePicker document={row} />
    </div>
  );
}

export function DocumentOwnerCell(props: GridCellParams<ReportGridRow>): JSX.Element {
  const cellClasses = useCellStyles();
  const { row } = props;

  if (!row.owner) return <div className={cellClasses.cell}>-</div>;

  return <OwnerCell row={{ owner: row.owner }} />;
}
